import { LogLevelDesc } from "loglevel";
import type { TaskRouter } from "./TaskRouter";
import { Supervisor, Workspace } from ".";
import { ClientEventEmitter } from "~/modules/client/ClientEventEmitter/ClientEventEmitter";
import { getConfigRegion, parseRegionForTwilsock } from "~/utils/regionUtil";
import { Logger, LoggerName, getLogger } from "~/modules/logger";
import { SessionImpl } from "~/modules/session/Session/SessionImpl";
import type { Session } from "~/modules/session/Session/Session";
import { ContextInstance, ContextManager } from "~/modules/contextManager/ContextManager";
import { ClientOptionsStore } from "~/modules/client/ClientOptions/ClientOptionsStore";
import { sendWorkerTrackingEvent } from "~/modules/telemetry/utils";
import { EVENTS } from "~/modules/telemetry/Analytics";
import { AnalyticsImpl } from "~/modules/telemetry/AnalyticsImpl";
import { ClientEvent } from "~/modules/client";

type LogLevelString = "error" | "warn" | "info" | "debug" | "trace" | "silent";




export class TaskRouterImpl implements TaskRouter {
    readonly #logger: Logger;

    readonly #session: Session;

    readonly #clientOptions: ClientOptionsStore;

    #ensureInitializedPromise: Promise<void> | undefined;

    #worker: Supervisor;

    #workspace: Workspace;

    readonly #analytics: AnalyticsImpl;

    readonly #ctx: ContextManager;

    #eventListenersRegistered: boolean;

    constructor(ctx: ContextManager) {
        this.#ctx = ctx;
        this.#session = ctx.retrieveInstanceOf(ContextInstance.SessionImpl, SessionImpl);
        this.#clientOptions = ctx.retrieveInstanceOf(ContextInstance.ClientOptionsStore, ClientOptionsStore);
        this.#logger = getLogger(ctx)(LoggerName.TaskRouter);
        this.#analytics = ctx.retrieveInstanceOf(ContextInstance.AnalyticsImpl, AnalyticsImpl);
        this.#eventListenersRegistered = false;
        this.#ensureInitializedPromise = undefined;
    }

    async init(): Promise<void> {
        if (this.#ensureInitializedPromise) {
            return this.#ensureInitializedPromise;
        }

        this.#registerEventListeners();

        const workerInstance = this.#clientOptions?.worker;
        const workspaceInstance = this.#clientOptions?.workspace;

        if (workerInstance) {
            this.#worker = workerInstance;
        }

        if (workspaceInstance) {
            this.#workspace = workspaceInstance;
        }

        if (this.#worker && this.#workspace) {
            this.#ensureInitializedPromise = Promise.resolve();
            return this.#ensureInitializedPromise;
        }

        const configRegion = getConfigRegion(this.#clientOptions);
        const parsedRegion = parseRegionForTwilsock(configRegion);

        if (!this.#worker) {
            const workerOptions = this.#clientOptions?.taskRouterOptions?.workerOptions ?? {};

            workerOptions.logLevel =
                workerOptions?.logLevel ?? this.mapToLogLevelString(this.#clientOptions?.logger?.level);

            if (configRegion !== undefined) {
                workerOptions.region = parsedRegion;
            }

            this.#worker = new Supervisor(this.#session.token, workerOptions);
        }

        let workspaceOptions = this.#clientOptions?.taskRouterOptions?.workspaceOptions;
        if (configRegion !== undefined) {
            workspaceOptions = {};
            workspaceOptions.region = parsedRegion;
        }

        if (!this.#workspace && workerInstance) {
            this.#workspace = new Workspace(this.#session.token, workspaceOptions, this.#worker.workspaceSid);
            this.#ensureInitializedPromise = Promise.resolve();
            return this.#ensureInitializedPromise;
        }

        this.#ensureInitializedPromise = new Promise((resolve) => {
            this.#worker.on("ready", () => {
                this.#workspace = new Workspace(this.#session.token, workspaceOptions, this.#worker.workspaceSid);
                resolve();
            });
        });

        return this.#ensureInitializedPromise;
    }

    updateToken(token: string): void {
        this.#logger.debug("updateToken called");

        if (this.#worker) {
            try {
                this.worker.updateToken(token);
            } catch (error) {
                this.#logger.error("Failed to update token on worker", error);
            }
        }

        if (this.#workspace) {
            try {
                this.workspace.updateToken(token);
            } catch (error) {
                this.#logger.error("Failed to update token on workspace", error);
            }
        }
        sendWorkerTrackingEvent(EVENTS.UpdateTokenCompleted, this.#analytics, this.worker);
        this.#logger.debug("updateToken completed");
    }

    destroy() {
        if (this.#ensureInitializedPromise) {
            this.#ensureInitializedPromise = undefined;
            this.worker.disconnect();
        }
    }

    async createTask(to: string, from: string, workflowSid: string, taskQueueSid: string, options = {}) {
        
        if (!this.#worker) {
            return Promise.reject(new Error("TaskRouter is not initialized"));
        }

        
        return this.#worker.createTask(to, from, workflowSid, taskQueueSid, options);
    }

    get worker(): Supervisor {
        return this.#worker;
    }

    get workspace(): Workspace {
        return this.#workspace;
    }

    private mapToLogLevelString(logLevel: LogLevelDesc): LogLevelString {
        const logLevelMapping: Record<string, number> = {
            trace: 0,
            debug: 1,
            info: 2,
            warn: 3,
            error: 4,
            silent: 5
        };

        if (typeof logLevel === "number") {
            switch (logLevel) {
                case 0:
                    return "trace";
                case 1:
                    return "debug";
                case 2:
                    return "info";
                case 3:
                    return "warn";
                case 4:
                    return "error";
                case 5:
                    return "silent";
                default:
                    return "warn";
            }
        } else if (typeof logLevel === "string") {
            const lowercaseLogLevel = logLevel.toLowerCase();
            if (lowercaseLogLevel in logLevelMapping) {
                return lowercaseLogLevel as LogLevelString;
            }
        }
        return "warn";
    }

    


    #registerEventListeners(): void {
        if (this.#eventListenersRegistered) return;
        this.#logger.debug("Taskrouter: Registering client event listeners");

        const clientEventEmitter = this.#ctx.retrieveInstanceOf(ContextInstance.ClientEventEmitter, ClientEventEmitter);

        clientEventEmitter.addListener(ClientEvent.TokenUpdated, (token: string) => this.updateToken(token));
        clientEventEmitter.addListener(ClientEvent.ClientDestroyed, () => this.destroy());
        this.#eventListenersRegistered = true;
    }
}
